// src/pages/CallbackPage.tsx
import { useEffect, useState, useRef } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { accountService } from '../services/accountService';
import { useAuth } from '../context/AuthContext';
import { Spin } from 'antd';
import {
  consumePostLoginRedirect,
  sanitizePostLoginRedirect,
} from '../utils/postLoginRedirect';

const CallbackPage = () => {
  const auth = useOidcAuth();
  const { login, setAccounts } = useAuth();
  const navigate = useNavigate();
  const [fetchError, setFetchError] = useState(null);
  const hasStartedFetch = useRef(false);

  const resolvePostLoginPath = () => {
    // Prefer OIDC state (passed from Login), fall back to sessionStorage deep link.
    const fromOidc = sanitizePostLoginRedirect(auth.user?.state, '');
    if (fromOidc) {
      try {
        sessionStorage.removeItem('clairai_post_login_redirect');
      } catch (_) {
        /* ignore */
      }
      return fromOidc;
    }
    return consumePostLoginRedirect('/dashboard');
  };

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user || hasStartedFetch.current) return;
    hasStartedFetch.current = true;
    setFetchError(null);

    const profile = auth.user.profile || {};
    const username =
      profile.name ||
      profile.preferred_username ||
      profile.email ||
      profile['cognito:username'] ||
      'user';
    const accessToken = auth.user.access_token;
    let defaultRole = 'Viewer';
    if (profile['custom:role'] != null && profile['custom:role'] !== undefined) {
      try {
        const parsed = JSON.parse(profile['custom:role']);
        defaultRole = Array.isArray(parsed) ? parsed[0] : parsed;
      } catch (_) {}
    }
    accountService
      .getAccounts()
      .then((data) => {
        const mappedDataArray = [];
        let firstClientId = null;
        if (data && typeof data === 'object') {
          const clientIds = Object.keys(data);
          firstClientId = clientIds.length > 0 ? clientIds[0] : null;
          for (const clientId in data) {
            for (const accountId in data[clientId]) {
              mappedDataArray.push({
                ...data[clientId][accountId],
                accountId,
                clientId,
              });
            }
          }
        }
        const client_id =
          firstClientId ||
          profile['custom:client_id'] ||
          profile['custom:clientId'] ||
          process.env.REACT_APP_DEFAULT_CLIENT_ID;

        if (!client_id) {
          setFetchError('Your account is missing a client id. Please contact an administrator.');
          return;
        }

        const user = {
          username,
          token: accessToken,
          role: defaultRole,
          client_id,
        };
        login(user);
        setAccounts(mappedDataArray);
        navigate(resolvePostLoginPath(), { replace: true });
      })
      .catch((error) => {
        console.error('Failed to fetch accounts:', error);
        setFetchError(error?.message || 'Failed to load accounts');
        const client_id =
          profile['custom:client_id'] ||
          profile['custom:clientId'] ||
          process.env.REACT_APP_DEFAULT_CLIENT_ID;
        if (!client_id) {
          // Without a client id we cannot safely call backend APIs.
          return;
        }
        const user = {
          username,
          token: accessToken,
          role: defaultRole,
          client_id,
        };
        login(user);
        setAccounts([]);
        navigate(resolvePostLoginPath(), { replace: true });
      });
  }, [auth.isAuthenticated, auth.user, login, setAccounts, navigate]);

  if (auth.error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ color: '#ff4d4f' }}>Sign-in error: {auth.error.message}</p>
        <a href="/login">Back to login</a>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ color: '#faad14' }}>{fetchError}</p>
        <a href="/dashboard">Continue to dashboard</a>
      </div>
    );
  }

  return (
    <div className="login-splash-overlay">
      <div className="login-splash-content">
        <img
          src="/Logo/LOGO 32X32.svg"
          alt="ClairAI logo"
          className="login-splash-logo"
        />
        <h2 className="login-splash-title">ClairAI</h2>
        <Spin size="large" className="login-splash-spinner" />
        <p className="login-splash-message">Loading your dashboard…</p>
      </div>
    </div>
  );
};

export default CallbackPage;

